import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { routes } from "../routes";
import { FeatureContainer } from "./RoundedContainer";

const PIPELINE_BREAKPOINT = 1100;

const PipelineContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding: 4rem 2rem;
    margin-top: 4rem;
    min-height: 400px;

    @media (max-width: ${PIPELINE_BREAKPOINT}px) {
        display: none;
    }
`;

const SimpleCardsContainer = styled.div`
    display: none;
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    padding: 2rem 1rem;
    margin-top: 2rem;

    @media (max-width: ${PIPELINE_BREAKPOINT}px) {
        display: flex;
    }
`;

const LeftCardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
`;

const RightCardContainer = styled.div`
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
`;

const CenterLogoContainer = styled.div`
    position: relative;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    margin: 0 4rem;
`;

const LogoContainer = styled.div<{ $isActive?: boolean }>`
    background: radial-gradient(circle, rgba(38,170,99,1) 36%, rgba(54,129,106,1) 100%);
    border-radius: 28px;
    width: 8rem;
    height: 8rem;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s ease;
    position: relative;

    img {
        filter: var(--filter-white);
        transition: transform 0.3s ease;
    }

    ${(props) =>
      props.$isActive
        ? `
        transform: scale(1.1);
        box-shadow: 0px 0px 32px 12px rgba(64, 177, 115, 0.5);
    `
        : ""}
`;

const InteractiveCard = styled(FeatureContainer)<{ $isHovered?: boolean }>`
    min-width: 240px;
    max-width: 320px;
    width: 100%;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    ${(props) =>
      props.$isHovered
        ? `
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(38, 170, 99, 0.2);
        border-color: var(--color-primary);
    `
        : ""}

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(38, 170, 99, 0.2);
        border-color: var(--color-primary);
    }
`;

const SimpleCard = styled(FeatureContainer)`
    min-width: 0;
    max-width: 400px;
    width: 100%;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--color-primary);
        box-shadow: 0 4px 16px rgba(38, 170, 99, 0.15);
    }
`;

const MetadataCard = styled(FeatureContainer)`
    min-width: 240px;
    opacity: 0.9;
    cursor: default;
`;

const SVGContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
    overflow: visible;
`;

const _particleFlow = keyframes`
    0% {
        offset-distance: 0%;
        opacity: 0;
    }
    10% {
        opacity: 1;
    }
    90% {
        opacity: 1;
    }
    100% {
        offset-distance: 100%;
        opacity: 0;
    }
`;

const AnimatedParticle = styled.circle`
    fill: #26AA63;
    r: 5;
    opacity: 0;
`;

interface BezierPathProps {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  controlX1: number;
  controlY1: number;
  controlX2: number;
  controlY2: number;
  isActive?: boolean;
  uniqueId: string;
}

const BezierPath: React.FC<BezierPathProps> = ({
  startX,
  startY,
  endX,
  endY,
  controlX1,
  controlY1,
  controlX2,
  controlY2,
  isActive,
  uniqueId,
}) => {
  const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

  return (
    <g>
      <defs>
        <marker
          id={`arrow-${uniqueId}`}
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            d="M0,0 L0,6 L9,3 z"
            fill="#26AA63"
            opacity={isActive ? 1 : 0.3}
          />
        </marker>
      </defs>
      <path
        id={`path-${uniqueId}`}
        d={pathData}
        stroke={isActive ? "#26AA63" : "rgba(38, 170, 99, 0.3)"}
        strokeWidth="2"
        fill="none"
        markerEnd={`url(#arrow-${uniqueId})`}
        style={{ transition: "stroke 0.3s ease" }}
      />
      {isActive &&
        [0, 1, 2].map((id) => (
          <AnimatedParticle key={id}>
            <animateMotion
              dur="2s"
              repeatCount="indefinite"
              begin={`${id * 0.6}s`}
            >
              <mpath href={`#path-${uniqueId}`} />
            </animateMotion>
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              dur="2s"
              begin={`${id * 0.6}s`}
              repeatCount="indefinite"
            />
          </AnimatedParticle>
        ))}
    </g>
  );
};

const FeaturePipeline = () => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const codeCardRef = useRef<HTMLDivElement>(null);
  const generalCardRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const metadataCardRef = useRef<HTMLDivElement>(null);
  const [pathData, setPathData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const updatePaths = () => {
      if (
        !containerRef.current ||
        !codeCardRef.current ||
        !generalCardRef.current ||
        !logoRef.current ||
        !metadataCardRef.current
      )
        return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const codeRect = codeCardRef.current.getBoundingClientRect();
      const generalRect = generalCardRef.current.getBoundingClientRect();
      const logoRect = logoRef.current.getBoundingClientRect();
      const metadataRect = metadataCardRef.current.getBoundingClientRect();

      // Calculate relative positions within container
      const codeStartX = codeRect.right - containerRect.left;
      const codeStartY = codeRect.top + codeRect.height / 2 - containerRect.top;
      const generalStartX = generalRect.right - containerRect.left;
      const generalStartY =
        generalRect.top + generalRect.height / 2 - containerRect.top;
      const logoLeftX = logoRect.left - containerRect.left;
      const logoRightX = logoRect.right - containerRect.left;
      const logoY = logoRect.top + logoRect.height / 2 - containerRect.top;
      const metadataStartX = metadataRect.left - containerRect.left;
      const metadataY =
        metadataRect.top + metadataRect.height / 2 - containerRect.top;

      const controlOffset = 100;

      setPathData({
        codeToLogo: {
          startX: codeStartX,
          startY: codeStartY,
          endX: logoLeftX,
          endY: logoY,
          controlX1: codeStartX + controlOffset,
          controlY1: codeStartY,
          controlX2: logoLeftX - controlOffset,
          controlY2: logoY,
        },
        generalToLogo: {
          startX: generalStartX,
          startY: generalStartY,
          endX: logoLeftX,
          endY: logoY,
          controlX1: generalStartX + controlOffset,
          controlY1: generalStartY,
          controlX2: logoLeftX - controlOffset,
          controlY2: logoY,
        },
        logoToMetadata: {
          startX: logoRightX,
          startY: logoY,
          endX: metadataStartX,
          endY: metadataY,
          controlX1: logoRightX + controlOffset,
          controlY1: logoY,
          controlX2: metadataStartX - controlOffset,
          controlY2: metadataY,
        },
        svgWidth: containerRect.width,
        svgHeight: containerRect.height,
      });
    };

    updatePaths();
    window.addEventListener("resize", updatePaths);
    const interval = setInterval(updatePaths, 100); // Update periodically for dynamic layouts

    return () => {
      window.removeEventListener("resize", updatePaths);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <PipelineContainer ref={containerRef}>
        {pathData && (
          <SVGContainer>
            <svg
              width={pathData.svgWidth}
              height={pathData.svgHeight}
              style={{ position: "absolute", top: 0, left: 0 }}
              aria-label="Pipeline flow diagram"
            >
              <BezierPath
                uniqueId="code-to-logo"
                {...pathData.codeToLogo}
                isActive={hoveredCard === "code"}
              />
              <BezierPath
                uniqueId="general-to-logo"
                {...pathData.generalToLogo}
                isActive={hoveredCard === "general"}
              />
              <BezierPath
                uniqueId="logo-to-metadata"
                {...pathData.logoToMetadata}
                isActive={hoveredCard !== null}
              />
            </svg>
          </SVGContainer>
        )}

        <LeftCardsContainer>
          <div ref={codeCardRef}>
            <InteractiveCard
              $isHovered={hoveredCard === "code"}
              onMouseEnter={() => setHoveredCard("code")}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => navigate(routes.annotationTechnical.absolute)}
            >
              <div>
                <h1>Code</h1>
                <img src="logo_code.svg" alt="Code" />
              </div>
              <p>
                Upload your Python script to generate technical metadata
                automatically
              </p>
            </InteractiveCard>
          </div>

          <div ref={generalCardRef}>
            <InteractiveCard
              $isHovered={hoveredCard === "general"}
              onMouseEnter={() => setHoveredCard("general")}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => navigate(routes.annotationGeneral.absolute)}
            >
              <div>
                <h1>General Info</h1>
                <img src="logo_code.svg" alt="General Info" />
              </div>
              <p>
                Fill out forms to create general metadata for your data and
                software
              </p>
            </InteractiveCard>
          </div>
        </LeftCardsContainer>

        <CenterLogoContainer>
          <div ref={logoRef}>
            <LogoContainer $isActive={hoveredCard !== null}>
              <img src="logo.svg" width="64" height="64" alt="DataDesc" />
            </LogoContainer>
          </div>
        </CenterLogoContainer>

        <RightCardContainer>
          <div ref={metadataCardRef}>
            <MetadataCard>
              <div>
                <h1>Metadata Sheet</h1>
                <img src="logo_metadata.svg" alt="Metadata" />
              </div>
              <p>Get a DataDesc compliant metadata description</p>
            </MetadataCard>
          </div>
        </RightCardContainer>
      </PipelineContainer>

      <SimpleCardsContainer>
        <SimpleCard
          onClick={() => navigate(routes.annotationTechnical.absolute)}
        >
          <div>
            <h1>Technical Metadata</h1>
            <img src="logo_code.svg" alt="Code" />
          </div>
          <p>
            Upload your Python script to generate technical metadata
            automatically
          </p>
        </SimpleCard>
        <SimpleCard onClick={() => navigate(routes.annotationGeneral.absolute)}>
          <div>
            <h1>General Metadata</h1>
            <img src="logo_code.svg" alt="General Info" />
          </div>
          <p>
            Fill out forms to create general metadata for your data and software
          </p>
        </SimpleCard>
      </SimpleCardsContainer>
    </>
  );
};

export default FeaturePipeline;
